<template>
  <div v-if="contract" class="api-contract">
    <div class="endpoint">
      <span class="method">{{ contract.request.method }}</span>
      <code>{{ contract.request.path }}</code>
      <span class="endpoint-note">Internal API · JSON</span>
    </div>

    <div class="exchange">
      <section class="message request" aria-label="API request">
        <header class="message-header">
          <span class="direction">Search → <strong>For-Me</strong></span>
          <h3>Request</h3>
          <p>The context Search sends</p>
        </header>
        <div class="payload-label">Request body · application/json</div>
        <pre><code>{{ formatJson(contract.request.body) }}</code></pre>
        <ul class="field-notes">
          <li v-for="note in contract.request.notes" :key="note.field">
            <code>{{ note.field }}</code>
            <span>{{ note.meaning }}</span>
          </li>
        </ul>
      </section>

      <section class="message response" aria-label="API response">
        <header class="message-header">
          <span class="direction"><strong>For-Me</strong> → Search</span>
          <h3>Response</h3>
          <p>Choose an outcome to compare</p>
        </header>
        <div class="response-options" role="group" aria-label="Response examples">
          <button
            v-for="(response, index) in contract.responses"
            :key="response.label"
            type="button"
            :aria-pressed="selectedIndex === index"
            @click="selectedIndex = index"
          >
            {{ response.label }}
          </button>
        </div>
        <div class="response-content" aria-live="polite" aria-atomic="true">
          <div class="payload-label">HTTP {{ selectedResponse.httpStatus }} · application/json</div>
          <pre><code>{{ formatJson(selectedResponse.body) }}</code></pre>
          <p class="outcome"><strong>Search does this:</strong> {{ selectedResponse.meaning }}</p>
        </div>
      </section>
    </div>
  </div>
  <pre v-else class="source-fallback"><code>{{ code }}</code></pre>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({ code: { type: String, default: '' } })
const selectedIndex = ref(0)
const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)

const contract = computed(() => {
  try {
    const data = JSON.parse(props.code)
    if (!isObject(data?.request?.body) || !Array.isArray(data.responses) || !data.responses.length) return null
    if (!data.responses.every((response) => isObject(response?.body))) return null
    return data
  } catch {
    return null
  }
})

const selectedResponse = computed(() => contract.value?.responses[selectedIndex.value] || contract.value?.responses[0])
watch(() => props.code, () => { selectedIndex.value = 0 })

// Keep each top-level field on its own line and nested values compact for projection.
function formatJson(body) {
  return `{
${Object.entries(body).map(([key, value]) => `  ${JSON.stringify(key)}: ${JSON.stringify(value)}`).join(',\n')}
}`
}
</script>

<style scoped>
.api-contract { margin: 1rem 0; }
.endpoint {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.65rem;
  margin-bottom: 0.85rem;
}
.method {
  padding: 0.2rem 0.55rem;
  border-radius: 5px;
  background: var(--teal-deep);
  color: white;
  font-size: 0.75rem;
  font-weight: 750;
  letter-spacing: 0.05em;
}
.endpoint > code { font-size: 1rem; font-weight: 650; }
.endpoint-note { color: var(--muted); font-size: 0.8rem; }
.exchange {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 20rem), 1fr));
  gap: 1rem;
  align-items: start;
}
.message {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-top: 4px solid var(--teal);
  border-radius: 10px;
  background: var(--cream);
}
.response { border-top-color: #216653; }
.message-header { padding: 0.85rem 1rem; }
.direction {
  color: var(--teal-deep);
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.04em;
}
.response .direction { color: #216653; }
.message-header h3 { margin: 0.15rem 0; font-size: 1.2rem; }
.message-header p { margin: 0; color: var(--muted); font-size: 0.85rem; }
.payload-label {
  padding: 0.45rem 1rem;
  background: var(--paper-warm);
  color: var(--ink-soft);
  font-size: 0.72rem;
  font-weight: 650;
}
pre {
  margin: 0;
  padding: 1rem;
  background: var(--ink);
  color: #eef5ff;
  font-size: 0.8rem;
  line-height: 1.7;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}
.field-notes { margin: 0; padding: 1rem; list-style: none; }
.field-notes li { display: grid; gap: 0.1rem; margin-bottom: 0.65rem; }
.field-notes li:last-child { margin-bottom: 0; }
.field-notes code { color: var(--teal-deep); font-size: 0.8rem; font-weight: 650; }
.field-notes span, .outcome { font-size: 0.85rem; line-height: 1.55; }
.response-options { display: flex; flex-wrap: wrap; gap: 0.4rem; padding: 0 1rem 0.8rem; }
.response-options button {
  padding: 0.4rem 0.65rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--cream);
  color: var(--ink-soft);
  font: inherit;
  font-size: 0.8rem;
  cursor: pointer;
}
.response-options button[aria-pressed="true"] {
  background: #e0f2ed;
  border-color: #216653;
  color: #153e33;
  font-weight: 700;
}
.response-options button:focus-visible { outline: 3px solid var(--teal); outline-offset: 3px; }
.outcome { margin: 0; padding: 0.9rem 1rem; }
.source-fallback { border-radius: 8px; }
</style>
